/**
 * Utility functions for generating BibTeX entries from citation data
 */

import type { CitationPaper } from '@/api/citations'

/**
 * Generate a BibTeX entry from a citation paper
 */
export function generateBibtexEntry(paper: CitationPaper, bibtexKey?: string): string {
  const key = bibtexKey || generateBibtexKey(paper)
  
  // Determine entry type based on venue/type
  const entryType = paper.arxiv_id ? 'article' : 'article' // Default to article
  
  // Format authors for BibTeX (separated by ' and ')
  const authors = paper.authors.length > 0
    ? paper.authors.join(' and ')
    : 'Unknown Author'
  
  // Build BibTeX entry
  let bibtex = `@${entryType}{${key},\n`
  bibtex += `  title={${paper.title}},\n`
  bibtex += `  author={${authors}},\n`
  
  if (paper.year) {
    bibtex += `  year={${paper.year}},\n`
  }
  
  if (paper.venue) {
    bibtex += `  journal={${paper.venue}},\n`
  }
  
  if (paper.doi) {
    bibtex += `  doi={${paper.doi}},\n`
  }
  
  if (paper.pmid) {
    bibtex += `  pmid={${paper.pmid}},\n`
  }
  
  if (paper.arxiv_id) {
    bibtex += `  eprint={${paper.arxiv_id}},\n`
    bibtex += `  archivePrefix={arXiv},\n`
  }
  
  if (paper.url) {
    bibtex += `  url={${paper.url}},\n`
  }
  
  // Remove trailing comma and close
  bibtex = bibtex.replace(/,\n$/, '\n')
  bibtex += '}'
  
  return bibtex
}

/**
 * Generate a BibTeX key from a citation paper
 */
export function generateBibtexKey(paper: CitationPaper): string {
  if (paper.doi) {
    // Use DOI-based key for uniqueness
    const doiKey = paper.doi.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    return doiKey.substring(0, 30)
  }
  
  // Format: first_author_lastname_year_title_words
  const firstAuthor = paper.authors.length > 0
    ? paper.authors[0].split(/\s+/).pop()?.toLowerCase() || 'unknown'
    : 'unknown'
  
  const year = paper.year ? String(paper.year) : 'noyear'
  const titleWords = paper.title
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.toLowerCase().replace(/[^a-z0-9]/g, ''))
    .join('')
  
  return `${firstAuthor}${year}${titleWords}`.substring(0, 30)
}

